/**
 * Vitrine d'un restaurant (« Mon établissement ») : constantes et validation
 * des liens saisis par le restaurateur.
 *
 * Règle de sécurité : un lien n'est accepté qu'en https://, à la saisie ET à
 * l'affichage. `normalizeHttpsUrl` / `validateGoogleReviewsUrl` servent au
 * formulaire (messages d'erreur en français) ; `safeHttpsUrl`,
 * `safeGoogleReviewsUrl` et `safeStorageImageUrl` servent à l'affichage
 * (retournent null si la valeur stockée n'est pas sûre, quelle que soit son origine).
 *
 * Miroir côté règles : `firestore.rules` (match /restaurants) et `storage.rules`.
 * L'app mobile (apps/mobile) devra embarquer une copie de ces fonctions.
 */

/** Nombre maximum de photos dans la galerie « plats ». */
export const MAX_DISH_PHOTOS = 12;

/** Taille maximale d'un fichier envoyé dans Storage (après compression). Miroir de storage.rules. */
export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Longueur maximale d'un lien (miroir de firestore.rules). */
export const MAX_URL_LENGTH = 500;

export type UrlCheck = { ok: true; url: string } | { ok: false; error: string };

const HTTPS_ONLY_ERROR = 'Seuls les liens sécurisés commençant par https:// sont acceptés.';

/**
 * Formes exactes acceptées pour le lien « avis Google » — miroir de
 * googleReviewsUrl() dans firestore.rules (à modifier ensemble).
 * Domaines en liste blanche (google.com / google.fr : un motif « google.xx »
 * accepterait des domaines qui n'appartiennent pas à Google) et formes de
 * chemin précises plutôt qu'un hôte accepté pour n'importe quel chemin.
 */
const GOOGLE_REVIEWS_URL_PATTERNS: readonly RegExp[] = [
    // https://www.google.fr/maps/place/… , https://google.com/maps?cid=…
    /^https:\/\/(www\.)?google\.(com|fr)\/maps([/?#][^\s]*)?$/,
    // https://maps.google.com/?cid=… , https://maps.google.fr/maps?q=…
    /^https:\/\/maps\.google\.(com|fr)(\/maps([/?#][^\s]*)?|\/?[?#][^\s]*|\/?)$/,
    // Bouton « Partager » de Google Maps : https://maps.app.goo.gl/AbC123?g_st=ic
    /^https:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9_-]+([?#][^\s]*)?$/,
    // Bouton « Partager » de la recherche Google : https://share.google/AbC123
    /^https:\/\/share\.google\/[A-Za-z0-9_-]+$/,
    // Fiche d'établissement : https://g.page/r/CabcDEF/review , https://g.page/mon-resto
    /^https:\/\/g\.page\/(r\/)?[A-Za-z0-9_-]+(\/review)?\/?([?#][^\s]*)?$/,
    // Panneau d'informations de la recherche : https://g.co/kgs/AbC123
    /^https:\/\/g\.co\/kgs\/[A-Za-z0-9_-]+$/,
    // https://search.google.com/local/writereview?placeid=… (et /local/reviews)
    /^https:\/\/search\.google\.com\/local\/(writereview|reviews)([?#][^\s]*)?$/,
];

/**
 * Google normalise côté serveur « .. », « %2e », « ; » et « \ » : un lien
 * « google.com/maps/..;/url?q=https://site-piege » aboutit à la redirection
 * ouverte google.com/url. Ces séquences et tout segment « /url » sont refusés.
 */
const GOOGLE_UNSAFE_SEQUENCE = /\.\.|%2e|;|\\/i;
const GOOGLE_REDIRECT_SEGMENT = /^https:\/\/[^/]+\/(.*\/)?url([/?#].*)?$/i;

/**
 * Hôtes ATABLE (app, hébergements Firebase, site) : jamais acceptés comme « site internet »
 * du restaurant. Ils ouvrent l'app (liens universels des puces /t?n=&s=) ; un lien de la
 * vitrine pointant dessus servirait à déclencher une fausse validation ou à imiter l'app.
 * Miroir de notAtableHost() dans firestore.rules (à modifier ensemble).
 */
// Hôtes Firebase composés (pas de littéral « <projet>.firebaseapp.com ») : le garde-fou
// scripts/check-hosting-build.mjs reconnaît le projet d'un bundle à ce motif.
const ATABLE_HOSTS: readonly string[] = [
    'atthetable.fr', 'atable.fr',
    ...['atable-114d4', 'atable-preprod'].flatMap((p) => ['web.app', 'firebaseapp.com'].map((s) => `${p}.${s}`)),
];

/** true pour un hôte ATABLE ou l'un de ses sous-domaines (point final ignoré). */
export function isAtableHost(hostname: string): boolean {
    const host = hostname.toLowerCase().replace(/\.$/, '');
    return ATABLE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/** URL de téléchargement Firebase Storage (seule source acceptée pour les photos). */
const STORAGE_DOWNLOAD_URL = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[A-Za-z0-9._-]+\/o\/[^\s"<>\\]+$/;

/**
 * Valide et normalise un lien saisi par le restaurateur.
 * - « monrestaurant.fr » → « https://monrestaurant.fr/ » (schéma ajouté) ;
 * - tout schéma explicite autre que https (http:, javascript:, data:, …) est refusé ;
 * - pas d'espaces, pas d'identifiants (user:pass@), un vrai nom de domaine.
 */
export function normalizeHttpsUrl(raw: string): UrlCheck {
    const input = raw.trim();
    if (!input) return { ok: false, error: 'Le lien est vide.' };
    if (input.length > MAX_URL_LENGTH) {
        return { ok: false, error: `Le lien est trop long (${MAX_URL_LENGTH} caractères maximum).` };
    }
    if (/[\s\u0000-\u001f<>"`\\]/.test(input)) {
        return { ok: false, error: 'Le lien contient des caractères non autorisés (espaces, guillemets…).' };
    }

    // Schéma explicite (« xxx: » non suivi d'un chiffre, pour ne pas confondre avec « site.fr:8080 »).
    const scheme = /^([a-z][a-z0-9+.-]*):(?!\d)/i.exec(input);
    let candidate = input;
    if (scheme) {
        if (scheme[1].toLowerCase() !== 'https') return { ok: false, error: HTTPS_ONLY_ERROR };
    } else {
        candidate = `https://${input.replace(/^\/+/, '')}`;
    }

    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return { ok: false, error: 'Ce lien n\'est pas valide.' };
    }
    if (url.protocol !== 'https:') return { ok: false, error: HTTPS_ONLY_ERROR };
    if (url.username || url.password) {
        return { ok: false, error: 'Le lien ne doit pas contenir d\'identifiant ni de mot de passe.' };
    }
    const host = url.hostname.toLowerCase();
    if (!host.includes('.') || host.endsWith('.') || host.startsWith('[') || /^[\d.]+$/.test(host)) {
        return { ok: false, error: 'Indiquez l\'adresse complète du site (ex. https://www.monrestaurant.fr).' };
    }
    if (isAtableHost(host)) {
        return { ok: false, error: 'Ce lien mène à l\'application ATABLE : indiquez l\'adresse de votre propre site.' };
    }

    const normalized = url.toString();
    if (normalized.length > MAX_URL_LENGTH) {
        return { ok: false, error: `Le lien est trop long (${MAX_URL_LENGTH} caractères maximum).` };
    }
    return { ok: true, url: normalized };
}

/** true si le lien normalisé (https) a l'une des formes de fiche Google acceptées. */
function isGoogleReviewsUrl(href: string): boolean {
    if (GOOGLE_UNSAFE_SEQUENCE.test(href) || GOOGLE_REDIRECT_SEGMENT.test(href)) return false;
    return GOOGLE_REVIEWS_URL_PATTERNS.some((pattern) => pattern.test(href));
}

/** Valide le lien vers la fiche Google du restaurant (formulaire). */
export function validateGoogleReviewsUrl(raw: string): UrlCheck {
    if (raw.trim().length > MAX_URL_LENGTH) {
        return {
            ok: false,
            error: 'Ce lien est trop long. Dans Google Maps, ouvrez la fiche de votre restaurant, '
                + 'touchez « Partager » puis « Copier le lien » pour obtenir un lien court.',
        };
    }
    const check = normalizeHttpsUrl(raw);
    if (!check.ok) return check;
    // La saisie brute est aussi contrôlée : la normalisation résout « .. » et « %2e ».
    if (GOOGLE_UNSAFE_SEQUENCE.test(raw) || !isGoogleReviewsUrl(check.url)) {
        return {
            ok: false,
            error: 'Ce lien ne mène pas à Google Maps. Collez le lien de votre fiche Google '
                + '(Google Maps → votre restaurant → « Partager » → « Copier le lien »).',
        };
    }
    return check;
}

/**
 * Lien sûr à afficher (bouton « Site internet ») ou null.
 * Exige un https:// explicite : ne « répare » pas une valeur stockée douteuse.
 */
export function safeHttpsUrl(value: unknown): string | null {
    if (typeof value !== 'string' || !/^https:\/\//i.test(value.trim())) return null;
    const check = normalizeHttpsUrl(value);
    return check.ok ? check.url : null;
}

/** Lien sûr vers la fiche Google (bouton « Voir nos avis Google ») ou null. */
export function safeGoogleReviewsUrl(value: unknown): string | null {
    if (typeof value === 'string' && GOOGLE_UNSAFE_SEQUENCE.test(value)) return null;
    const url = safeHttpsUrl(value);
    return url && isGoogleReviewsUrl(url) ? url : null;
}

/**
 * Photo de la vitrine sûre à afficher, ou null : uniquement une URL de
 * téléchargement Firebase Storage (pas d'image externe pouvant servir au pistage).
 */
export function safeStorageImageUrl(value: unknown): string | null {
    return typeof value === 'string' && STORAGE_DOWNLOAD_URL.test(value) ? value : null;
}
